// Nyheter (Ansatte → Nyheter, Anders 03.11): en leder skriver en nyhet og velger hvem som skal få
// den. Den vises i meldingsbanneret på kontrollpanelet deres (ny-kundesamhandling/Ansattmeldinger.jsx),
// i kø med beskjedene fra klubbinfo, til de merker den som lest eller datoen er passert. Kan også
// sendes som SMS, men bare for viktige og hastende nyheter. Under skjemaet står de sendte nyhetene
// med hvem som har lest dem, og de kan brukes igjen eller avsluttes. Backenden sjekker at det er en
// leder (src/routes/ansattmeldinger.js).
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Cookies from 'js-cookie';
import { Tekstfelt, feltKlasse, knappPrimar, knappSekundar } from '../ny-kundesamhandling/Skjema';
import Ikon from '../ny-kundesamhandling/Ikon';
import { Meldingskort } from '../ny-kundesamhandling/Ansattmeldinger';
import { Melding } from './tjenester/Velgere';
import { useErLeder, BUTIKK_VALG, paPause } from '../employeeAccess';

const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/+$/, '');
const DAG_MS = 24 * 60 * 60 * 1000;

async function kall(sti, metode = 'GET', data) {
  const res = await fetch(`${API_BASE_URL}/${sti}`, {
    method: metode,
    headers: {
      'X-Ansatt': encodeURIComponent(Cookies.get('selectedEmployee') || ''),
      ...(data ? { 'Content-Type': 'application/json' } : {}),
    },
    body: data ? JSON.stringify(data) : undefined,
    cache: 'no-store',
  });
  const svar = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(svar.message || 'Noe gikk galt. Prøv igjen.');
  return svar;
}

// yyyy-mm-dd i lokal tid, for <input type="date">.
const datoFelt = (d) => {
  const x = new Date(d);
  return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`;
};
const kortDato = (d) => new Date(d).toLocaleDateString('nb-NO', { day: 'numeric', month: 'short' });
const tidspunkt = (d) => new Date(d).toLocaleString('nb-NO', { dateStyle: 'short', timeStyle: 'short' });
const fornavn = (navn) => String(navn || '').trim().split(/\s+/)[0] || '';

// Samme oppbygging som smsTekst i src/routes/ansattmeldinger.js.
const smsTekst = ({ tittel, tekst, hilsen }) => [tittel.trim(), tekst.trim(), hilsen.trim()].filter(Boolean).join('\n\n');
// Omtrent hvor mange SMS-er teksten blir: 160 tegn (153 per del) med vanlige tegn, også æøå;
// 70 (67 per del) når den har tegn utenfor SMS-alfabetet, f.eks. «» og emoji.
const GSM = /^[\n\r A-Za-z0-9@£$¥èéùìòÇØøÅåÆæßÉÄÖÑÜäöñüà§¿¡!"#%&'()*+,\-./:;<=>?_]*$/;
function smsDeler(tekst) {
  if (!tekst) return 0;
  const [en, per] = GSM.test(tekst) ? [160, 153] : [70, 67];
  return tekst.length <= en ? 1 : Math.ceil(tekst.length / per);
}

const tomtSkjema = (dager = 7) => ({
  tittel: '',
  tekst: '',
  hilsen: fornavn(Cookies.get('selectedEmployee')) ? `Hilsen ${fornavn(Cookies.get('selectedEmployee'))}` : '',
  visTil: datoFelt(Date.now() + dager * DAG_MS),
  mottakere: [],
  sms: false,
});

function visningsstatus(n) {
  if (n.avsluttet || !n.aktiv) {
    return { tekst: `Avsluttet${n.avsluttet ? ` ${kortDato(n.avsluttet)}` : ''}${n.avsluttetAv ? ` av ${fornavn(n.avsluttetAv)}` : ''}`, aktiv: false };
  }
  if (n.visTil && new Date(n.visTil) <= new Date()) return { tekst: `Utløpt ${kortDato(n.visTil)}`, aktiv: false };
  return { tekst: `Vises til og med ${kortDato(n.visTil)}`, aktiv: true };
}

// Mottakerne gruppert etter butikk, i samme rekkefølge som under Ansatte. De som er på pause, er
// grået ut og kommer ikke med i «Velg alle», men kan krysses av for hånd (Anders 03.11).
function Mottakere({ ansatte, valgte, onEndre }) {
  const grupper = useMemo(() => {
    const rekke = [...BUTIKK_VALG, ...new Set(ansatte.map((a) => a.butikk || 'Uten butikk'))];
    return [...new Set(rekke)]
      .map((butikk) => ({
        butikk,
        ansatte: ansatte
          .filter((a) => (a.butikk || 'Uten butikk') === butikk)
          .sort((a, b) => (a.navn || '').localeCompare(b.navn || '', 'nb')),
      }))
      .filter((g) => g.ansatte.length);
  }, [ansatte]);
  const alle = ansatte.map((a) => a._id);
  const aktive = ansatte.filter((a) => !paPause(a)).map((a) => a._id);
  const antallPause = alle.length - aktive.length;
  const sett = (ider, pa) => onEndre(pa ? [...new Set([...valgte, ...ider])] : valgte.filter((id) => !ider.includes(id)));

  return (
    <fieldset className="min-w-0 space-y-3">
      <legend className="font-semibold text-slate-800">
        Mottakere <span className="font-normal text-slate-600">({valgte.length} av {alle.length} valgt)</span>
      </legend>
      {antallPause > 0 && (
        <p className="text-sm text-slate-600">
          {antallPause} {antallPause === 1 ? 'ansatt er' : 'ansatte er'} på pause. De er grået ut og kommer ikke med i
          «Velg alle», men du kan krysse dem av for hånd.
        </p>
      )}
      <div className="flex flex-wrap gap-2">
        <button type="button" className={knappSekundar} onClick={() => onEndre([...new Set([...valgte, ...aktive])])}>Velg alle</button>
        <button type="button" className={knappSekundar} onClick={() => onEndre([])} disabled={!valgte.length}>Fjern alle</button>
      </div>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {grupper.map((g, gi) => {
          const ider = g.ansatte.filter((a) => !paPause(a)).map((a) => a._id);
          const alleValgt = ider.length > 0 && ider.every((id) => valgte.includes(id));
          return (
            <div key={g.butikk} role="group" aria-labelledby={`ny-gruppe-${gi}`} className="min-w-0 space-y-2 rounded-xl border border-slate-200 bg-white p-2.5">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h4 id={`ny-gruppe-${gi}`} className="font-semibold text-slate-900">{g.butikk}</h4>
                {ider.length > 0 && <button
                  type="button"
                  onClick={() => sett(ider, !alleValgt)}
                  aria-label={`${alleValgt ? 'Fjern alle' : 'Velg alle'} i ${g.butikk}`}
                  className="rounded-lg px-2 py-1 text-sm font-semibold text-indigo-700 hover:bg-indigo-50 focus:outline-none focus-visible:ring-4 focus-visible:ring-indigo-600/50"
                >
                  {alleValgt ? 'Fjern alle' : 'Velg alle'}
                </button>}
              </div>
              <ul className="space-y-1">
                {g.ansatte.map((a) => (
                  <li key={a._id} className="flex min-w-0 items-center gap-2">
                    <input
                      id={`ny-a-${a._id}`}
                      type="checkbox"
                      checked={valgte.includes(a._id)}
                      onChange={(e) => sett([a._id], e.target.checked)}
                      className="h-5 w-5 shrink-0 rounded border-2 border-slate-400 accent-emerald-700"
                    />
                    <label htmlFor={`ny-a-${a._id}`} className={`min-w-0 [overflow-wrap:anywhere] ${paPause(a) ? 'text-slate-500' : ''}`}>
                      {a.navn}
                      {paPause(a) && (
                        <span className="ml-1.5 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-900">Pause</span>
                      )}
                      {a.tilgang === 'leder' && (
                        <span className="ml-1.5 rounded-full bg-rose-100 px-2 py-0.5 text-xs font-semibold text-rose-800">Leder</span>
                      )}
                    </label>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>
    </fieldset>
  );
}

// «Send som SMS også», med advarselen og hvor mange SMS-er det blir.
function SmsValg({ skjema, onEndre, ansatte, maksTegn }) {
  const tekst = smsTekst(skjema);
  const deler = smsDeler(tekst);
  const valgte = ansatte.filter((a) => skjema.mottakere.includes(a._id));
  const utenNummer = valgte.filter((a) => !String(a.telefon || '').trim()).length;
  const forLang = tekst.length > maksTegn;
  return (
    <div className="min-w-0 space-y-2 rounded-xl border-2 border-amber-300 bg-amber-50 p-3">
      <div className="flex items-start gap-2">
        <input
          id="ny-sms"
          type="checkbox"
          checked={skjema.sms}
          onChange={(e) => onEndre(e.target.checked)}
          aria-describedby="ny-sms-advarsel"
          className="mt-0.5 h-5 w-5 shrink-0 rounded border-2 border-slate-400 accent-amber-700"
        />
        <label htmlFor="ny-sms" className="font-semibold text-slate-900">Send som SMS også</label>
      </div>
      <p id="ny-sms-advarsel" className="flex items-start gap-2 text-amber-950">
        <Ikon navn="varsel" className="mt-0.5 w-5 h-5 shrink-0" />
        <span>
          <strong>Bare for viktige og hastende nyheter.</strong> Alle mottakerne får hele nyheten på mobilen, også
          når de har fri. Den kan ikke trekkes tilbake, og hver SMS koster. Vanlige nyheter holder det med
          kontrollpanelet.
        </span>
      </p>
      {skjema.sms && (
        <p className={`text-sm ${forLang ? 'font-semibold text-red-800' : 'text-slate-800'}`} role={forLang ? 'alert' : undefined}>
          {tekst.length} av {maksTegn} tegn · {deler} SMS per mottaker
          {valgte.length > 0 && ` · ${valgte.length - utenNummer} av ${valgte.length} mottakere har mobilnummer`}
          {forLang && '. For lang til SMS: kort den ned, eller send uten SMS.'}
        </p>
      )}
    </div>
  );
}

function smsLinje(sms) {
  if (!sms) return '';
  const deler = [`SMS til ${sms.antall}`];
  if (sms.utenNummer) deler.push(`${sms.utenNummer} uten nummer`);
  if (sms.feilet) deler.push(`${sms.feilet} ikke sendt`);
  return deler.join(', ');
}

function Logg({ nyheter, onBrukIgjen, onAvslutt }) {
  const [bekreft, setBekreft] = useState('');
  if (!nyheter.length) {
    return <p className="rounded-xl border border-dashed border-slate-300 px-3 py-5 text-slate-700">Ingen nyheter sendt ennå.</p>;
  }
  return (
    <ul className="space-y-3">
      {nyheter.map((n) => {
        const s = visningsstatus(n);
        const totalt = (n.mottakere || []).length;
        const lest = (n.lest || []).length;
        return (
          <li key={n._id}>
            <article aria-labelledby={`ny-logg-${n._id}`} className="min-w-0 space-y-2 rounded-2xl border-2 border-slate-200 bg-white p-3">
              <div className="flex flex-wrap items-center gap-2">
                <h4 id={`ny-logg-${n._id}`} className="min-w-0 flex-1 basis-48 font-bold text-slate-900 [overflow-wrap:anywhere]">{n.tittel}</h4>
                <span className={`rounded-full px-2 py-0.5 text-sm font-semibold ${s.aktiv ? 'bg-emerald-100 text-emerald-900' : 'bg-slate-100 text-slate-700'}`}>
                  {s.tekst}
                </span>
              </div>
              <p className="text-sm text-slate-600">
                Sendt {tidspunkt(n.createdAt)} av {n.opprettetAv || 'ukjent'} · til {totalt} {totalt === 1 ? 'ansatt' : 'ansatte'}
                {n.sms ? ` · ${smsLinje(n.sms)}` : ''}
              </p>
              <p className="font-semibold text-slate-900">
                {lest} av {totalt} har lest
              </p>
              <p className="line-clamp-3 whitespace-pre-line text-slate-800 [overflow-wrap:anywhere]">{n.tekst}</p>
              {(n.ikkeLest || []).length > 0 && (
                <details className="text-sm">
                  <summary className="cursor-pointer font-semibold text-slate-800">Har ikke lest ({n.ikkeLest.length})</summary>
                  <ul className="mt-1 flex flex-wrap gap-1.5">
                    {n.ikkeLest.map((navn) => (
                      <li key={navn} className="rounded-full bg-amber-100 px-2 py-0.5 text-amber-950">{navn}</li>
                    ))}
                  </ul>
                </details>
              )}
              {lest > 0 && (
                <details className="text-sm">
                  <summary className="cursor-pointer font-semibold text-slate-800">Har lest ({lest})</summary>
                  <ul className="mt-1 flex flex-wrap gap-1.5">
                    {n.lest.map((l) => (
                      <li key={l.navn} className="rounded-full bg-emerald-50 px-2 py-0.5 text-emerald-950">
                        {l.navn} · {tidspunkt(l.dato)}
                      </li>
                    ))}
                  </ul>
                </details>
              )}
              <div className="flex flex-wrap gap-2">
                <button type="button" className={knappSekundar} onClick={() => onBrukIgjen(n)}>
                  <Ikon navn="gjenta" className="w-5 h-5" /> Bruk igjen
                </button>
                {s.aktiv && bekreft !== n._id && (
                  <button type="button" className={knappSekundar} onClick={() => setBekreft(n._id)}>
                    Avslutt nå
                  </button>
                )}
                {s.aktiv && bekreft === n._id && (
                  <span role="group" aria-label={`Avslutte «${n.tittel}»?`} className="flex flex-wrap items-center gap-2">
                    <span className="text-slate-800">Fjerne den fra kontrollpanelet til alle?</span>
                    <button type="button" autoFocus className={`${knappSekundar} border-red-300 text-red-800`} onClick={() => { setBekreft(''); onAvslutt(n); }}>
                      Ja, avslutt
                    </button>
                    <button type="button" className={knappSekundar} onClick={() => setBekreft('')}>Avbryt</button>
                  </span>
                )}
              </div>
            </article>
          </li>
        );
      })}
    </ul>
  );
}

export default function Nyheter() {
  const leder = useErLeder();
  const [ansatte, setAnsatte] = useState([]);
  const [logg, setLogg] = useState(null);
  const [grenser, setGrenser] = useState({ maks: { tittel: 120, tekst: 2000, hilsen: 100, smsTegn: 612 }, maksDager: 60, standardDager: 7 });
  const [skjema, setSkjema] = useState(() => tomtSkjema());
  const [status, setStatus] = useState({ type: '', tekst: '' });
  const [bekreftSms, setBekreftSms] = useState(false);
  const [feil, setFeil] = useState('');

  const hent = useCallback(async () => {
    if (!leder) return;
    try {
      const [a, l] = await Promise.all([kall('employees'), kall('ansattmeldinger/nyheter')]);
      setAnsatte(a.filter((x) => x.butikk && x.butikk !== 'Ingen'));
      setLogg(l.nyheter);
      setGrenser({ maks: l.maks, maksDager: l.maksDager, standardDager: l.standardDager });
      setFeil('');
    } catch (err) {
      setFeil(err.message);
    }
  }, [leder]);
  useEffect(() => { hent(); }, [hent]);

  const endre = (endring) => {
    setSkjema((s) => ({ ...s, ...endring }));
    setBekreftSms(false);
    setStatus((st) => (st.type === 'feil' ? { type: '', tekst: '' } : st));
  };
  const felt = (navn) => (e) => endre({ [navn]: e.target.value });

  async function send(e) {
    e.preventDefault();
    if (!skjema.mottakere.length) return setStatus({ type: 'feil', tekst: 'Velg minst én mottaker.' });
    if (skjema.sms && smsTekst(skjema).length > grenser.maks.smsTegn) {
      return setStatus({ type: 'feil', tekst: 'For lang til SMS. Kort den ned, eller fjern haken for SMS.' });
    }
    // SMS kan ikke trekkes tilbake: ett ekstra trykk først.
    if (skjema.sms && !bekreftSms) return setBekreftSms(true);
    setBekreftSms(false);
    setStatus({ type: 'sender', tekst: '' });
    try {
      const n = await kall('ansattmeldinger/nyheter', 'POST', skjema);
      const antall = n.mottakere.length;
      setStatus({
        type: n.sms?.feilet ? 'feil' : 'ok',
        tekst: `Sendt til ${antall} ${antall === 1 ? 'ansatt' : 'ansatte'}. De ser den på kontrollpanelet.${n.sms ? ` ${smsLinje(n.sms)}.` : ''}`,
      });
      setSkjema(tomtSkjema(grenser.standardDager));
      setLogg((l) => [n, ...(l || [])]);
    } catch (err) {
      setStatus({ type: 'feil', tekst: err.message });
    }
  }

  function brukIgjen(n) {
    const finnes = new Set(ansatte.map((a) => a._id));
    const mottakere = (n.mottakere || []).map((x) => String(x.ansattId)).filter((id) => finnes.has(id));
    const borte = (n.mottakere || []).length - mottakere.length;
    setSkjema({
      tittel: n.tittel,
      tekst: n.tekst,
      hilsen: n.hilsen || '',
      visTil: datoFelt(Date.now() + grenser.standardDager * DAG_MS),
      mottakere,
      sms: false,
    });
    setBekreftSms(false);
    setStatus({
      type: 'info',
      tekst: `Fylt inn fra «${n.tittel}». Se over teksten og mottakerne før du sender.${borte ? ` ${borte} av mottakerne finnes ikke lenger.` : ''}`,
    });
    const tittel = document.getElementById('ny-tittel');
    tittel?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    tittel?.focus({ preventScroll: true });
  }

  async function avslutt(n) {
    try {
      const ny = await kall(`ansattmeldinger/nyheter/${n._id}/avslutt`, 'POST');
      setLogg((l) => l.map((x) => (x._id === ny._id ? ny : x)));
    } catch (err) {
      setFeil(err.message);
      hent();
    }
  }

  const idag = datoFelt(Date.now());
  const sender = status.type === 'sender';
  const forhandsvisning = {
    kilde: 'nyhet',
    fra: fornavn(Cookies.get('selectedEmployee')),
    tittel: skjema.tittel || 'Overskrift',
    tekst: skjema.tekst || 'Nyheten kommer her.',
    hilsen: skjema.hilsen,
  };
  const antall = skjema.mottakere.length;

  return (
    <div className="space-y-6">
      <div className="space-y-1">
        <h2 className="text-2xl font-semibold">Nyheter</h2>
        <p className="text-slate-700">
          Skriv en nyhet til de ansatte du velger. Den vises i meldingsbanneret på kontrollpanelet deres til de har lest den.
        </p>
      </div>

      {!leder && <Melding>Bare en leder kan sende nyheter. Velg deg selv øverst i menyen.</Melding>}
      {feil && <Melding type="feil">{feil}</Melding>}

      {leder && (
        <form onSubmit={send} aria-labelledby="ny-ny" className="space-y-4 rounded-2xl border-2 border-slate-200 bg-slate-50 p-3">
          <h3 id="ny-ny" className="text-lg font-bold text-slate-900">Ny nyhet</h3>
          <div className="grid gap-4 lg:grid-cols-2">
            <div className="min-w-0 space-y-3">
              <Tekstfelt id="ny-tittel" etikett="Overskrift" required maxLength={grenser.maks.tittel}
                value={skjema.tittel} onChange={felt('tittel')} />
              <Tekstfelt id="ny-tekst" etikett="Nyheten" flerlinjet rows={6} required maxLength={grenser.maks.tekst}
                hjelp="Tom linje gir nytt avsnitt." value={skjema.tekst} onChange={felt('tekst')} />
              <Tekstfelt id="ny-hilsen" etikett="Hilsen" maxLength={grenser.maks.hilsen}
                value={skjema.hilsen} onChange={felt('hilsen')} />
              <div className="min-w-0">
                <label htmlFor="ny-vistil" className="mb-1 block font-semibold text-slate-800">Vises til og med</label>
                <input id="ny-vistil" type="date" required min={idag} max={datoFelt(Date.now() + grenser.maksDager * DAG_MS)}
                  value={skjema.visTil} onChange={felt('visTil')} aria-describedby="ny-vistil-hjelp" className={`${feltKlasse} max-w-[12rem]`} />
                <p id="ny-vistil-hjelp" className="mt-1 text-sm text-slate-600">Forsvinner før det for den som merker den som lest.</p>
              </div>
            </div>
            <div className="min-w-0 space-y-2">
              <h4 className="font-semibold text-slate-800">Slik ser den ut</h4>
              <Meldingskort melding={forhandsvisning} forhandsvisning>
                <div className="flex justify-end">
                  <span aria-hidden="true" className="rounded-xl border-2 border-sky-300 bg-white px-3 py-1.5 font-semibold text-sky-900">Merk som lest</span>
                </div>
              </Meldingskort>
            </div>
          </div>

          {ansatte.length > 0 && <Mottakere ansatte={ansatte} valgte={skjema.mottakere} onEndre={(mottakere) => endre({ mottakere })} />}

          <SmsValg skjema={skjema} ansatte={ansatte} maksTegn={grenser.maks.smsTegn} onEndre={(sms) => endre({ sms })} />

          {status.type === 'feil' && <Melding type="feil">{status.tekst}</Melding>}
          {status.type === 'ok' && <Melding type="ok">{status.tekst}</Melding>}
          {status.type === 'info' && <Melding>{status.tekst}</Melding>}
          {bekreftSms && (
            <p role="alert" className="rounded-xl border-2 border-amber-400 bg-amber-50 px-3 py-2 font-semibold text-amber-950">
              Nyheten går også som SMS til {antall} {antall === 1 ? 'ansatt' : 'ansatte'}. Trykk «Ja, send med SMS» for å sende.
            </p>
          )}
          <div className="flex flex-wrap gap-2">
            <button type="submit" disabled={sender} className={knappPrimar}>
              <Ikon navn="send" className="w-6 h-6" />
              {sender ? 'Sender …' : bekreftSms ? 'Ja, send med SMS' : `Send${antall ? ` til ${antall}` : ''}${skjema.sms ? ' + SMS' : ''}`}
            </button>
            {bekreftSms && (
              <button type="button" className={knappSekundar} onClick={() => setBekreftSms(false)}>Avbryt</button>
            )}
            <button type="button" className={knappSekundar} onClick={() => { setSkjema(tomtSkjema(grenser.standardDager)); setBekreftSms(false); setStatus({ type: '', tekst: '' }); }}>
              Tøm skjemaet
            </button>
          </div>
        </form>
      )}

      {leder && (
        <section aria-labelledby="ny-logg" className="space-y-3">
          <h3 id="ny-logg" className="flex items-center gap-2 text-lg font-bold text-slate-900">
            <Ikon navn="historikk" className="w-6 h-6" /> Sendte nyheter
          </h3>
          {logg === null && !feil && (
            <p className="rounded-xl border border-dashed border-slate-300 px-3 py-5 text-slate-700">Henter nyhetene …</p>
          )}
          {logg && <Logg nyheter={logg} onBrukIgjen={brukIgjen} onAvslutt={avslutt} />}
        </section>
      )}
    </div>
  );
}
